import {
  ITaskData,
  ITaskTypes,
  logger,
  navigation,
  task,
  errorForm,
} from '@origin-digital/reporting-client';
import * as React from 'react';
import { navigateToErrorPage } from 'ts/passwordless-login/components/pages/error/ErrorNavigation';
import { isValidPassword } from 'ts/passwordless-login/services/passwordvalidation/PasswordValidationService';
import * as Auth0StateAssistant from 'ts/services/auth0/Auth0StateAssistant';
import { getAuth0State } from '../../../../services/auth0/StateService';
import PageConfig from '../../../../services/userflow/PageConfig';

import { getWindow } from 'ts/window/Window';
import { resetPassword } from '../../../services/changepassword/ChangePasswordService';
import { SetYourPasswordView } from './SetYourPasswordView';
import { ERROR_CODES } from '../error/ErrorCodes';

const PASSWORD_TOO_COMMON_ERROR = 'PasswordDictionaryError';
const PASSWORD_CONTAINS_USER_INFO_ERROR = 'PasswordNoUserInfoError';

interface SetYourPasswordPageState {
  password: string;
  spinner: boolean;
  passwordErrorMessage: string;
}

interface SetYourPasswordPageProps {
  config: PageConfig;
  nextAction?: () => void;
}

class SetYourPasswordPage extends React.PureComponent<
  SetYourPasswordPageProps,
  SetYourPasswordPageState
> {
  public state = {
    password: '',
    spinner: false,
    passwordErrorMessage: '',
  };

  public componentDidMount() {
    navigation('screen', {
      currentUri: '',
      friendlyUri: `originenergy:my-account:auth:${this.props.config.analytics.pageName}`,
    });
  }

  public render() {
    return (
      <SetYourPasswordView
        onDoneClick={this.resetPassword}
        onPasswordChanged={this.passwordChanged}
        password={this.state.password}
        username={getWindow().username} // comes from html file, injected by auth0
        showPasswordErrorMessage={!isValidPassword(this.state.password)}
        spinner={this.state.spinner}
        alertBoxErrorMessage={this.state.passwordErrorMessage}
      />
    );
  }

  private passwordChanged = (e) => this.setState({ password: e.target.value });

  private resetPassword = async (e) => {
    e.preventDefault();
    if (!isValidPassword(this.state.password)) {
      return;
    }

    this.setState({
      spinner: true,
      passwordErrorMessage: '',
    });

    try {
      const response = await resetPassword(
        this.state.password,
        getWindow().csrfToken,
        getWindow().resetPasswordTicket,
      );
      task.base(
        this.props.config.analytics.taskName as ITaskTypes,
        { action: 'complete' } as ITaskData,
      );
      if (this.props.nextAction) {
        this.props.nextAction();
      } else {
        Auth0StateAssistant.readStateIfExistsAndRedirect(getAuth0State(), response.data.result_url);
      }
    } catch (error) {
      this.handleError(error);
    }
  };

  private handleError = (error) => {
    logger.error('Could not reset password.', error);
    const { response = {} } = error || {};
    const { code, name } = response.data || {};
    if (code === 'invalid_password') {
      const formName = this.props.config.analytics.taskName;
      const errorCode = this.errorNameToAnalyticsMessage(name);
      errorForm({
        formName,
        errorMessage: `${formName}:${errorCode}`,
        errorCode,
      });
      this.setState({
        passwordErrorMessage: this.getPasswordErrorMessage(name),
        spinner: false,
      });
    } else {
      navigateToErrorPage(
        ERROR_CODES.SET_PW_ERROR,
        this.props.config.errorType,
        getWindow().location.href,
      );
    }
  };

  private errorNameToAnalyticsMessage = (name): string => {
    switch (name) {
      case PASSWORD_TOO_COMMON_ERROR:
        return 'password-too-common';
      case PASSWORD_CONTAINS_USER_INFO_ERROR:
        return 'password-contains-user-info';
      default:
        return 'password-error';
    }
  };

  private getPasswordErrorMessage = (name): string => {
    switch (name) {
      case PASSWORD_TOO_COMMON_ERROR:
        return 'Password is not allowed, it might be too common. Please try another password.';
      case PASSWORD_CONTAINS_USER_INFO_ERROR:
        return 'Password contains user information. Please try another password.';
      default:
        return 'Password is not allowed. Please try another password.';
    }
  };
}

export { SetYourPasswordPage };
